import {gql} from '@apollo/client';
import {useQuery} from '@apollo/client';
import {useState, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {
  Form,
  Table,
  Tag,
  Typography,
  Card,
  Button,
  Row,
  Col,
  Select,
  Space,
} from 'antd';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import moment from 'moment';
import styles from './salesAgentProspect.module.scss';
import {toast} from 'react-toastify';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';

const {Title, Text} = Typography;

const CALL_PRIORITY_LEVEL_OPTIONS = [
  {value: 'VERY_HIGH', label: 'Very High'},
  {value: 'HIGH', label: 'High'},
  {value: 'STANDARD', label: 'Standard'},
  {value: 'LOW', label: 'Low'},
  {value: 'RETENTION', label: 'Retention'},
];

const CALL_PRIORITY_LEVEL_TAG_STYLE = {
  VERY_HIGH: {emoji: '🔥', label: 'Very High', color: 'red'},
  HIGH: {emoji: '🔥', label: 'High', color: 'orange'},
  MODERATE: {emoji: '⚡', label: 'Standard', color: 'gold'},
  STANDARD: {emoji: '⚡', label: 'Standard', color: 'green'},
  LOW: {emoji: '💤', label: 'Low', color: 'default'},
  RETENTION: {emoji: '📌', label: 'Retention', color: 'purple'},
};

export const SalesAgentProspect = () => {
  const [appliedAgentId, setAppliedAgentId] = useState(null);
  const [appliedCallPriorityLevels, setAppliedCallPriorityLevels] = useState(
    [],
  );
  const isMountedRef = useRef(true);
  const user = useSelector(state => state?.auth?.userData?.user);
  const userRole = user?.activeAdminTab?.name;
  const loggedInUserId = user?.id;
  const [form] = Form.useForm();
  const draftAgentId = Form.useWatch('agentId', form);

  const isSuperAdminOrAdmin = ['SUPERADMIN', 'ADMIN'].includes(userRole);
  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.SalesAgentProspect),
  );

  const prospectQueryInput = useMemo(() => {
    const input = {};
    if (isSuperAdminOrAdmin) {
      if (appliedAgentId) {
        input.agentId = appliedAgentId.split(';')[2];
      }
    } else if (loggedInUserId) {
      input.agentId = loggedInUserId;
    }
    if (appliedCallPriorityLevels?.length > 0) {
      input.callPriorityLevels = appliedCallPriorityLevels;
    }
    return input;
  }, [
    isSuperAdminOrAdmin,
    appliedAgentId,
    loggedInUserId,
    appliedCallPriorityLevels,
  ]);

  const {data, loading, refetch} = useQuery(SALES_AGENT_PROSPECT_QUERY, {
    variables: {
      input: prospectQueryInput,
    },
    skip: isSuperAdminOrAdmin && !appliedAgentId,
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
    onError: error => toast.error(error.message),
  });

  const exportTableHeaders = [
    {label: 'Rank', key: 'rank'},
    {label: 'Call Priority', key: 'callPriority'},
    {label: 'Customer Name', key: 'customerName'},
    {label: 'Email', key: 'email'},
    {label: 'Primary Agent', key: 'primaryAgent'},
    {label: 'Has Primary Agent', key: 'hasPrimaryAgent'},
    {label: 'Primary Is Another Agent', key: 'isPrimaryAgentAnotherAgent'},
    {label: 'Wallet (XOF)', key: 'walletXof'},
    {label: 'Segments', key: 'segments'},
    {label: 'Last Interaction', key: 'lastInteraction'},
    {label: 'Last Trade', key: 'lastTrade'},
    {label: 'Last Deposit', key: 'lastDeposit'},
    {label: 'Opportunity Notes', key: 'opportunityNotes'},
    {label: 'Next Best Action', key: 'nextBestAction'},
    {label: 'Lead Score', key: 'leadScore'},
    {label: 'Retention Score', key: 'retentionScore'},
    {label: 'Retention Reasons', key: 'retentionReasons'},
    {label: 'Days Since Last Interaction', key: 'daysSinceLastInteraction'},
    {label: 'Days Since Last VCA', key: 'daysSinceLastVCA'},
    {label: 'Last VCA Date', key: 'lastVCADate'},
    {label: 'Daba Pro Status', key: 'dabaProStatus'},
    {label: 'Daba Pro Expiry Date', key: 'dabaProExpiryDate'},
    {label: 'Portfolio Negative', key: 'isPortfolioNegative'},
  ];

  const generateExportData = () => {
    return data?.getSalesAgentProspects?.data?.map((prospect, index) => {
      const priorityConfig = {
        VERY_HIGH: '🔥 Very High',
        HIGH: '🔥 High',
        MODERATE: '⚡ Standard',
        STANDARD: '⚡ Standard',
        LOW: '💤 Low',
        RETENTION: '📌 Retention',
      };
      // Use callPriorityLevel if available, otherwise fall back to callPriority
      const priorityValue =
        prospect?.callPriorityLevel || prospect?.callPriority;
      const priorityLabel =
        priorityConfig[priorityValue] ||
        priorityValue?.replace(/_/g, ' ') ||
        '-';

      const primaryAgent = prospect?.primaryAgent;
      const primaryAgentDisplay = primaryAgent
        ? [primaryAgent?.firstName, primaryAgent?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() ||
          primaryAgent?.email ||
          '-'
        : '-';

      return {
        rank: index + 1,
        callPriority: priorityLabel,
        customerName: prospect?.customerName || '-',
        email: prospect?.email || '-',
        primaryAgent: primaryAgentDisplay,
        hasPrimaryAgent:
          prospect?.hasPrimaryAgent != null
            ? prospect.hasPrimaryAgent
              ? 'Yes'
              : 'No'
            : '-',
        isPrimaryAgentAnotherAgent:
          prospect?.isPrimaryAgentAnotherAgent != null
            ? prospect.isPrimaryAgentAnotherAgent
              ? 'Yes'
              : 'No'
            : '-',
        walletXof: prospect?.walletXof
          ? Number(prospect.walletXof).toLocaleString('en-US')
          : '-',
        segments: Array.isArray(prospect?.segments)
          ? prospect.segments.map(s => s.replaceAll('_', ' ')).join(', ')
          : prospect?.segments || '-',
        lastInteraction: prospect?.lastInteraction
          ? moment(prospect.lastInteraction).format('YYYY-MM-DD')
          : '—',
        lastTrade: prospect?.lastTrade
          ? moment(prospect.lastTrade).format('YYYY-MM-DD')
          : '—',
        lastDeposit: prospect?.lastDeposit
          ? moment(prospect.lastDeposit).format('YYYY-MM-DD')
          : '—',
        opportunityNotes: prospect?.opportunityNotes || '-',
        nextBestAction: prospect?.nextBestAction || '-',
        leadScore: prospect?.leadScore || '-',
        retentionScore: prospect?.retentionScore || '-',
        retentionReasons: Array.isArray(prospect?.retentionReasons)
          ? prospect.retentionReasons
              .map(r => r.replaceAll('_', ' '))
              .join(', ')
          : prospect?.retentionReasons || '-',
        daysSinceLastInteraction:
          prospect?.daysSinceLastInteraction != null
            ? prospect.daysSinceLastInteraction
            : '-',
        daysSinceLastVCA:
          prospect?.daysSinceLastVCA != null ? prospect.daysSinceLastVCA : '-',
        lastVCADate: prospect?.lastVCADate
          ? moment(prospect.lastVCADate).format('YYYY-MM-DD')
          : '—',
        dabaProStatus: prospect?.dabaProStatus || '-',
        dabaProExpiryDate: prospect?.dabaProExpiryDate
          ? moment(prospect.dabaProExpiryDate).format('YYYY-MM-DD')
          : '—',
        isPortfolioNegative:
          prospect?.isPortfolioNegative != null
            ? prospect.isPortfolioNegative
              ? 'Yes'
              : 'No'
            : '-',
      };
    });
  };

  const handleExport = () => {
    const exportTableData = generateExportData();
    const csvHeaders = exportTableHeaders.map(h => h.label).join(',');
    const csvRows = exportTableData.map(row =>
      exportTableHeaders
        .map(header => {
          const value = row[header.key] || '';
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(','),
    );
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const exportFilename = `Sales Agent Prospects ${moment().format(
      'YYYY-MM-DD_HHmm',
    )}.csv`;
    link.setAttribute('download', exportFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFilterSubmit = values => {
    if (!isMountedRef.current) return;
    if (isSuperAdminOrAdmin) {
      setAppliedAgentId(values.agentId ?? null);
    }
    setAppliedCallPriorityLevels(
      Array.isArray(values.callPriorityLevels) ? values.callPriorityLevels : [],
    );
  };

  const handleReset = () => {
    if (!isMountedRef.current) return;
    form.resetFields();
    setAppliedAgentId(null);
    setAppliedCallPriorityLevels([]);
    if (isMountedRef.current) {
      refetch({
        input: {},
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Call Priority',
        key: 'callPriorityLevel',
        dataIndex: 'callPriorityLevel',
        render: priority => {
          if (!priority) return '-';
          const config = CALL_PRIORITY_LEVEL_TAG_STYLE[priority] || {
            label: priority.replace(/_/g, ' '),
            color: 'default',
            emoji: '',
          };
          return (
            <Tag color={config.color} className={styles.priorityTag}>
              {config.emoji ? `${config.emoji} ` : ''}
              {config.label}
            </Tag>
          );
        },
        fixed: 'left',
        width: 140,
      },
      {
        title: 'Customer',
        key: 'customer',
        dataIndex: 'customerName',
        render: (name, record) => {
          const userId = record?.customerId;
          const customerName = name || '-';
          const email = record?.email || '-';
          return (
            <div
              className={`${styles.userCell} ${userId ? styles.clickable : ''}`}
              onClick={() =>
                userId &&
                window.open(`/dashboard/user/${userId}/interactions`, '_blank')
              }>
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <span className={styles.userName}>{customerName}</span>
                <span className={styles.userEmail}>{email}</span>
              </div>
            </div>
          );
        },
        fixed: 'left',
        width: 200,
      },
      {
        title: 'Primary Agent',
        key: 'primaryAgent',
        dataIndex: 'primaryAgent',
        render: agent => {
          if (!agent) return '-';
          const name = [agent?.firstName, agent?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
          return (
            <span className={styles.segmentsText}>
              {name || agent?.email || '-'}
            </span>
          );
        },
        width: 160,
      },
      {
        title: 'Has Primary Agent',
        key: 'hasPrimaryAgent',
        dataIndex: 'hasPrimaryAgent',
        render: value => {
          if (value === null || value === undefined) return '-';
          return (
            <Tag
              color={value ? 'green' : 'default'}
              className={styles.statusTag}>
              {value ? 'Yes' : 'No'}
            </Tag>
          );
        },
        width: 130,
        align: 'center',
      },
      {
        title: 'Assigned to Another Agent',
        key: 'isPrimaryAgentAnotherAgent',
        dataIndex: 'isPrimaryAgentAnotherAgent',
        render: value => {
          if (value === null || value === undefined) return '-';
          return (
            <Tag
              color={value ? 'green' : 'default'}
              className={styles.statusTag}>
              {value ? 'Yes' : 'No'}
            </Tag>
          );
        },
        width: 150,
        align: 'center',
      },
      {
        title: 'Wallet (XOF)',
        key: 'walletXof',
        dataIndex: 'walletXof',
        render: amount => {
          if (amount === null || amount === undefined) return '-';
          return (
            <span className={styles.amount}>
              {Number(amount).toLocaleString('en-US')}
            </span>
          );
        },
        width: 130,
        align: 'right',
      },
      {
        title: 'Segments',
        key: 'segments',
        dataIndex: 'segments',
        render: segments => {
          if (!segments || segments.length === 0) return '-';
          return (
            <span className={styles.segmentsText}>
              {Array.isArray(segments)
                ? segments
                    .map(segment => segment.replaceAll('_', ' '))
                    .join(', ')
                : segments}
            </span>
          );
        },
        width: 250,
      },
      {
        title: 'Last Interaction',
        key: 'lastInteraction',
        dataIndex: 'lastInteraction',
        render: date => {
          if (!date) return <span className={styles.emptyDate}>—</span>;
          return (
            <span className={styles.date}>
              {moment(date).format('DD MMM, YYYY hh:mm A')}
            </span>
          );
        },
        width: 140,
      },
      {
        title: 'Last Trade',
        key: 'lastTrade',
        dataIndex: 'lastTrade',
        render: date => {
          if (!date) {
            return <span className={styles.emptyDate}>—</span>;
          }
          return (
            <span className={styles.date}>
              {moment(date).format('DD MMM, YYYY hh:mm A')}
            </span>
          );
        },
        width: 140,
      },
      {
        title: 'Last Deposit',
        key: 'lastDeposit',
        dataIndex: 'lastDeposit',
        render: date => {
          if (!date) return <span className={styles.emptyDate}>—</span>;
          return (
            <span className={styles.date}>
              {moment(date).format('DD MMM, YYYY hh:mm A')}
            </span>
          );
        },
        width: 240,
      },
      {
        title: 'Opportunity Notes',
        key: 'opportunityNotes',
        dataIndex: 'opportunityNotes',
        render: notes => <p className={styles.notes}>{notes || '-'}</p>,
        width: 500,
      },
      {
        title: 'Next Best Action',
        key: 'nextBestAction',
        dataIndex: 'nextBestAction',
        render: action => (
          <span className={styles.action}>{action || '-'}</span>
        ),
        width: 250,
      },
      {
        title: 'Lead Score',
        key: 'leadScore',
        dataIndex: 'leadScore',
        render: score => {
          if (score === null || score === undefined) return '-';
          const scoreColor =
            score >= 80
              ? 'green'
              : score >= 60
              ? 'gold'
              : score >= 40
              ? 'orange'
              : 'red';
          return (
            <Tag color={scoreColor} className={styles.leadScore}>
              {score}
            </Tag>
          );
        },
        width: 100,
        align: 'center',
      },
      {
        title: 'Retention Score',
        key: 'retentionScore',
        dataIndex: 'retentionScore',
        render: score => {
          if (score === null || score === undefined) return '-';
          const scoreColor =
            score >= 80
              ? 'green'
              : score >= 60
              ? 'gold'
              : score >= 40
              ? 'orange'
              : 'red';
          return (
            <Tag color={scoreColor} className={styles.leadScore}>
              {score}
            </Tag>
          );
        },
        width: 120,
        align: 'center',
      },
      {
        title: 'Retention Reasons',
        key: 'retentionReasons',
        dataIndex: 'retentionReasons',
        render: reasons => {
          if (!reasons || (Array.isArray(reasons) && reasons.length === 0))
            return '-';
          return (
            <span className={styles.segmentsText}>
              {Array.isArray(reasons)
                ? reasons.map(reason => reason.replaceAll('_', ' ')).join(', ')
                : reasons}
            </span>
          );
        },
        width: 200,
      },
      {
        title: 'Days Since Last Interaction',
        key: 'daysSinceLastInteraction',
        dataIndex: 'daysSinceLastInteraction',
        render: days => {
          if (days === null || days === undefined) return '-';
          return <span className={styles.days}>{days}</span>;
        },
        width: 180,
        align: 'right',
      },
      {
        title: 'Days Since Last VCA',
        key: 'daysSinceLastVCA',
        dataIndex: 'daysSinceLastVCA',
        render: days => {
          if (days === null || days === undefined) return '-';
          return <span className={styles.days}>{days}</span>;
        },
        width: 150,
        align: 'right',
      },
      {
        title: 'Last VCA Date',
        key: 'lastVCADate',
        dataIndex: 'lastVCADate',
        render: date => {
          if (!date) return <span className={styles.emptyDate}>—</span>;
          return (
            <span className={styles.date}>
              {moment(date).format('DD MMM, YYYY')}
            </span>
          );
        },
        width: 130,
      },
      {
        title: 'Daba Pro Status',
        key: 'dabaProStatus',
        dataIndex: 'dabaProStatus',
        render: status => {
          if (!status) return '-';
          const statusUpper = (status || '').toUpperCase();
          const statusConfig = {
            ACTIVE: {color: 'green', label: 'Active'},
            INACTIVE: {color: 'default', label: 'Inactive'},
            EXPIRED: {color: 'red', label: 'Expired'},
          };
          const config = statusConfig[statusUpper] || {
            color: 'default',
            label: status,
          };
          return (
            <Tag color={config.color} className={styles.statusTag}>
              {config.label}
            </Tag>
          );
        },
        width: 120,
        align: 'center',
      },
      {
        title: 'Daba Pro Expiry Date',
        key: 'dabaProExpiryDate',
        dataIndex: 'dabaProExpiryDate',
        render: date => {
          if (!date) return <span className={styles.emptyDate}>—</span>;
          return (
            <span className={styles.date}>
              {moment(date).format('DD MMM, YYYY')}
            </span>
          );
        },
        width: 160,
      },
      {
        title: 'Portfolio Negative',
        key: 'isPortfolioNegative',
        dataIndex: 'isPortfolioNegative',
        render: isNegative => {
          if (isNegative === null || isNegative === undefined) return '-';
          return (
            <Tag
              color={isNegative ? 'red' : 'green'}
              className={styles.statusTag}>
              {isNegative ? 'Yes' : 'No'}
            </Tag>
          );
        },
        width: 130,
        align: 'center',
      },
    ],
    [],
  );

  const showTable = isSuperAdminOrAdmin ? appliedAgentId : true;

  return (
    <div className={styles.salesAgentProspect}>
      <div className={styles.header}>
        <Title level={2}>Sales Agent Prospects</Title>
        <Text type="secondary">
          View and manage customer prospects for sales agents
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFilterSubmit}
        className={styles.filterForm}>
        {isSuperAdminOrAdmin && (
          <Card className={styles.filterCard}>
            <Row gutter={[16, 16]} align="bottom" className={styles.filterRow}>
              <Col xs={24} md={12} lg={10} xl={9}>
                <Form.Item
                  label="Sales Agent"
                  name="agentId"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a sales agent',
                    },
                  ]}>
                  <UserSelectField
                    users={draftAgentId}
                    mode="single"
                    setUsers={value => form.setFieldValue('agentId', value)}
                    placeholder="Search and Select Agent"
                    admin={true}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={9} xl={8}>
                <Form.Item
                  label="Call priority levels"
                  name="callPriorityLevels">
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All levels"
                    options={CALL_PRIORITY_LEVEL_OPTIONS}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24} lg={5} xl={7}>
                <Form.Item
                  label=" "
                  colon={false}
                  className={styles.filterActionsItem}>
                  <Space
                    direction="vertical"
                    size={8}
                    className={styles.filterActions}>
                    <Button type="primary" htmlType="submit" block>
                      Apply Filter
                    </Button>
                    <Button type="button" onClick={handleReset} block>
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {!isSuperAdminOrAdmin && (
          <Card className={styles.filterCard}>
            <Row gutter={[16, 16]} align="bottom" className={styles.filterRow}>
              <Col xs={24} md={14} lg={12} xl={10}>
                <Form.Item
                  label="Call priority levels"
                  name="callPriorityLevels">
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All levels"
                    options={CALL_PRIORITY_LEVEL_OPTIONS}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={10} lg={8} xl={6}>
                <Form.Item
                  label=" "
                  colon={false}
                  className={styles.filterActionsItem}>
                  <Space
                    direction="vertical"
                    size={8}
                    className={styles.filterActions}>
                    <Button type="primary" htmlType="submit" block>
                      Apply Filter
                    </Button>
                    <Button type="button" onClick={handleReset} block>
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}
      </Form>

      {showTable && (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div>
              <Title level={4}>Prospects List</Title>
              <Text type="secondary">
                {data?.getSalesAgentProspects?.data?.length} prospect
                {data?.getSalesAgentProspects?.data?.length !== 1
                  ? 's'
                  : ''}{' '}
                found
              </Text>
            </div>
            {pageOperations?.export && (
              <Button onClick={handleExport}>Export Prospects</Button>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={data?.getSalesAgentProspects?.data}
            loading={loading}
            className="antd-table"
            pagination={true}
            rowKey={(record, index) => record?.customerId || index}
          />
        </div>
      )}

      {isSuperAdminOrAdmin && !appliedAgentId && (
        <div className={styles.emptyState}>
          <Text type="secondary">
            Please select a sales agent to view prospects
          </Text>
        </div>
      )}
    </div>
  );
};

const SALES_AGENT_PROSPECT_QUERY = gql`
  query GetSalesAgentProspects($input: SalesAgentProspectInput!) {
    getSalesAgentProspects(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSalesAgentProspects {
        data {
          callPriority
          callPriorityLevel
          customerName
          email
          walletXof
          aumXof
          segments
          lastInteraction
          lastTrade
          lastDeposit
          opportunityNotes
          opportunityNotesFr
          nextBestAction
          nextBestActionFr
          leadScore
          customerId
          primaryAgent {
            firstName
            lastName
            email
          }
          retentionScore
          retentionReasons
          daysSinceLastInteraction
          daysSinceLastVCA
          lastVCADate
          dabaProStatus
          dabaProExpiryDate
          isPortfolioNegative
          isPrimaryAgentAnotherAgent
          hasPrimaryAgent
        }
      }
    }
  }
`;
